export type TextConstraintKey =
  | 'user.name'
  | 'user.email'
  | 'user.password'
  | 'auth.verificationCode'
  | 'auth.businessEmail'
  | 'merchant.name'
  | 'merchant.publicName'
  | 'merchant.description'
  | 'merchant.defaultReturnUrl'
  | 'merchant.apiKeyName'
  | 'merchant.webhookUrl'
  | 'merchant.webhookSecret'
  | 'merchant.statusReason'
  | 'merchant.verificationNotes'
  | 'merchant.terminationReason'
  | 'merchantChain.rejectReason'
  | 'order.metadataKey'
  | 'order.metadataValue'
  | 'refund.reason'
  | 'refund.notes'
  | 'admin.note';

export interface TextConstraintConfig {
  readonly key: TextConstraintKey;
  readonly label: string;
  readonly required?: boolean;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly trim?: boolean;
  readonly normalizeWhitespace?: boolean;
  readonly allowNewlines?: boolean;
  readonly transform?: 'lowercase' | 'uppercase';
  readonly pattern?: RegExp;
  readonly patternDescription?: string;
  readonly blocklist?: RegExp[];
  readonly disallowControlChars?: boolean;
}

// Reusable regular expressions
const UNICODE_NAME_PATTERN = /^[\p{L}\p{N}\p{M} .,&()'"\-_/]+$/u;
const UNICODE_DESCRIPTION_PATTERN = /^[\p{L}\p{N}\p{M}\p{Sc}\p{Pd}\p{Pc}\p{Sk}\p{Sm}\p{So}\p{Zs}\p{Pi}\p{Pf}\p{Ps}\p{Pe}\p{Po}\r\n]+$/u;
const API_KEY_NAME_PATTERN = /^[\p{L}\p{N}\p{M} _\-.()]+$/u;
const WEBHOOK_SECRET_PATTERN = /^[A-Za-z0-9._\-+=:/@!#%*?&]+$/;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/;
const HEX_ID_PATTERN = /^mch_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const METADATA_KEY_PATTERN = /^[A-Za-z0-9_.-]+$/;
const METADATA_VALUE_PATTERN = /^[\p{L}\p{N}\p{P}\p{Zs}\r\n]+$/u;

export const TEXT_CONSTRAINTS: Record<TextConstraintKey, TextConstraintConfig> = {
  'user.name': {
    key: 'user.name',
    label: 'username',
    required: true,
    minLength: 3,
    maxLength: 32,
    trim: true,
    normalizeWhitespace: false,
    pattern: /^[\p{L}\p{N}\p{M}_-]+$/u,
    patternDescription: 'letters, numbers, underscores, and hyphens only',
    disallowControlChars: true,
  },
  'user.email': {
    key: 'user.email',
    label: 'email',
    required: true,
    minLength: 6,
    maxLength: 254,
    trim: true,
    transform: 'lowercase',
    pattern: EMAIL_PATTERN,
    patternDescription: 'standard email format',
    disallowControlChars: true,
  },
  'auth.businessEmail': {
    key: 'auth.businessEmail',
    label: 'business email',
    required: false,
    minLength: 6,
    maxLength: 254,
    trim: true,
    transform: 'lowercase',
    pattern: EMAIL_PATTERN,
    patternDescription: 'standard email format',
    disallowControlChars: true,
  },
  'user.password': {
    key: 'user.password',
    label: 'password',
    required: true,
    minLength: 8,
    maxLength: 128,
    trim: false,
    disallowControlChars: false,
  },
  'auth.verificationCode': {
    key: 'auth.verificationCode',
    label: 'verification code',
    required: true,
    minLength: 6,
    maxLength: 6,
    trim: true,
    pattern: /^\d{6}$/,
    patternDescription: 'six digit code',
  },
  'merchant.name': {
    key: 'merchant.name',
    label: 'merchant name',
    required: true,
    minLength: 2,
    maxLength: 80,
    trim: true,
    normalizeWhitespace: true,
    pattern: UNICODE_NAME_PATTERN,
    patternDescription: 'letters, numbers, spaces, and basic punctuation (._-/&"\'())',
    disallowControlChars: true,
  },
  'merchant.publicName': {
    key: 'merchant.publicName',
    label: 'merchant public name',
    required: false,
    minLength: 2,
    maxLength: 100,
    trim: true,
    normalizeWhitespace: true,
    pattern: UNICODE_NAME_PATTERN,
    patternDescription: 'letters, numbers, spaces, and basic punctuation (._-/&"\'())',
    disallowControlChars: true,
  },
  'merchant.description': {
    key: 'merchant.description',
    label: 'merchant description',
    required: false,
    minLength: 0,
    maxLength: 280,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: UNICODE_DESCRIPTION_PATTERN,
    patternDescription: 'letters, numbers, spaces, and descriptive punctuation',
    disallowControlChars: true,
  },
  'merchant.defaultReturnUrl': {
    key: 'merchant.defaultReturnUrl',
    label: 'default return url',
    required: false,
    minLength: 0,
    maxLength: 512,
    trim: true,
    pattern: /^https:\/\/[\w\-._~:/?#\[\]@!$&'()*+,;=%]+$/i,
    patternDescription: 'must be an HTTPS URL without spaces',
    disallowControlChars: true,
  },
  'merchant.apiKeyName': {
    key: 'merchant.apiKeyName',
    label: 'API key name',
    required: true,
    minLength: 3,
    maxLength: 50,
    trim: true,
    normalizeWhitespace: true,
    pattern: API_KEY_NAME_PATTERN,
    patternDescription: 'letters, numbers, spaces, and basic separators (._-())',
    disallowControlChars: true,
  },
  'merchant.webhookUrl': {
    key: 'merchant.webhookUrl',
    label: 'webhook URL',
    required: true,
    minLength: 10,
    maxLength: 512,
    trim: true,
    pattern: /^https:\/\/[\w\-._~:/?#\[\]@!$&'()*+,;=%]+$/i,
    patternDescription: 'must be an HTTPS URL without spaces',
    disallowControlChars: true,
  },
  'merchant.webhookSecret': {
    key: 'merchant.webhookSecret',
    label: 'webhook secret',
    required: true,
    minLength: 16,
    maxLength: 128,
    trim: true,
    normalizeWhitespace: false,
    pattern: WEBHOOK_SECRET_PATTERN,
    patternDescription: 'letters, numbers, and safe punctuation (._-+=:/@!#%*?&)',
    disallowControlChars: true,
  },
  'merchant.statusReason': {
    key: 'merchant.statusReason',
    label: 'status reason',
    required: false,
    minLength: 3,
    maxLength: 500,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: UNICODE_DESCRIPTION_PATTERN,
    patternDescription: 'letters, numbers, spaces, and descriptive punctuation',
    disallowControlChars: true,
  },
  'merchant.verificationNotes': {
    key: 'merchant.verificationNotes',
    label: 'verification notes',
    required: false,
    minLength: 0,
    maxLength: 500,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: UNICODE_DESCRIPTION_PATTERN,
    patternDescription: 'letters, numbers, spaces, and descriptive punctuation',
    disallowControlChars: true,
  },
  'merchant.terminationReason': {
    key: 'merchant.terminationReason',
    label: 'termination reason',
    required: false,
    minLength: 3,
    maxLength: 500,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: UNICODE_DESCRIPTION_PATTERN,
    patternDescription: 'letters, numbers, spaces, and descriptive punctuation',
    disallowControlChars: true,
  },
  'merchantChain.rejectReason': {
    key: 'merchantChain.rejectReason',
    label: 'merchant chain rejection reason',
    required: true,
    minLength: 3,
    maxLength: 500,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: UNICODE_DESCRIPTION_PATTERN,
    patternDescription: 'letters, numbers, spaces, and descriptive punctuation',
    disallowControlChars: true,
  },
  'order.metadataKey': {
    key: 'order.metadataKey',
    label: 'metadata key',
    required: true,
    minLength: 1,
    maxLength: 100,
    trim: true,
    normalizeWhitespace: false,
    pattern: METADATA_KEY_PATTERN,
    patternDescription: 'letters, numbers, dot, underscore, and hyphen',
    disallowControlChars: true,
  },
  'order.metadataValue': {
    key: 'order.metadataValue',
    label: 'metadata value',
    required: true,
    minLength: 1,
    maxLength: 1000,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: METADATA_VALUE_PATTERN,
    patternDescription: 'letters, numbers, spaces, and common punctuation',
    disallowControlChars: true,
  },
  'refund.reason': {
    key: 'refund.reason',
    label: 'refund reason',
    required: true,
    minLength: 3,
    maxLength: 500,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: UNICODE_DESCRIPTION_PATTERN,
    patternDescription: 'letters, numbers, spaces, and descriptive punctuation',
    disallowControlChars: true,
  },
  'refund.notes': {
    key: 'refund.notes',
    label: 'refund notes',
    required: false,
    minLength: 0,
    maxLength: 500,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: UNICODE_DESCRIPTION_PATTERN,
    patternDescription: 'letters, numbers, spaces, and descriptive punctuation',
    disallowControlChars: true,
  },
  'admin.note': {
    key: 'admin.note',
    label: 'admin note',
    required: false,
    minLength: 0,
    maxLength: 500,
    trim: true,
    normalizeWhitespace: true,
    allowNewlines: true,
    pattern: UNICODE_DESCRIPTION_PATTERN,
    patternDescription: 'letters, numbers, spaces, and descriptive punctuation',
    disallowControlChars: true,
  },
};

export function getTextConstraint(key: TextConstraintKey): TextConstraintConfig {
  return TEXT_CONSTRAINTS[key];
}

export const BUSINESS_ID_PATTERNS = {
  merchant: HEX_ID_PATTERN,
};
